import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  Activity,
  AlertCircle,
  CheckSquare,
  FileSearch,
  Flag,
  ImageOff,
  Landmark,
  LifeBuoy,
  MailWarning,
  RefreshCw,
  Send,
  ShieldAlert,
  UserX,
} from 'lucide-react';
import { useAuditEvents, useOverviewBadges } from '../hooks/useAdminOps';
import { StatusChip, type ChipTone } from '../components/ui';
import type { OverviewQueueKey, QueueBadge } from '../api/adminOps';

interface QueueTile {
  key: OverviewQueueKey;
  label: string;
  description: string;
  to: string;
  icon: typeof Flag;
}

/** Every overview badge key maps to the queue page that works it. */
const QUEUE_TILES: QueueTile[] = [
  {
    key: 'reports_pending',
    label: 'Pending Reports',
    description: 'User reports awaiting review',
    to: '/moderation',
    icon: Flag,
  },
  {
    key: 'flagged_content',
    label: 'Flagged Content',
    description: 'Auto-flagged posts and comments',
    to: '/moderation',
    icon: ShieldAlert,
  },
  {
    key: 'ocr_manual_review',
    label: 'Document Review',
    description: 'Receipts needing manual OCR review',
    to: '/document-review',
    icon: FileSearch,
  },
  {
    key: 'fraud_review',
    label: 'Fraud Review',
    description: 'Donors with donations flagged by the velocity detector',
    to: '/fraud-review',
    icon: ShieldAlert,
  },
  {
    key: 'irs_reviews_pending',
    label: 'IRS Sync Reviews',
    description: 'Organization changes held for review',
    to: '/organizations',
    icon: Landmark,
  },
  {
    key: 'approvals_pending',
    label: 'Pending Approvals',
    description: 'Dual-control actions awaiting a checker',
    to: '/approvals',
    icon: CheckSquare,
  },
  {
    key: 'support_new',
    label: 'New Support Requests',
    description: 'Unworked support inbox items',
    to: '/support',
    icon: LifeBuoy,
  },
  {
    key: 'deletion_requests_active',
    label: 'Deletion Requests',
    description: 'Active account-deletion requests',
    to: '/compliance',
    icon: UserX,
  },
  {
    key: 'quarantine',
    label: 'Email Quarantine',
    description: 'Quarantined outbound emails',
    to: '/operations/queues',
    icon: MailWarning,
  },
  {
    key: 'outbox_failed',
    label: 'Failed Outbox',
    description: 'Org-status events that failed delivery',
    to: '/operations/queues',
    icon: Send,
  },
  {
    key: 'avatars_stranded',
    label: 'Stranded Avatars',
    description: 'Avatar uploads stuck mid-pipeline',
    to: '/operations/queues',
    icon: ImageOff,
  },
  {
    key: 'recompute_pending',
    label: 'Recompute Backlog',
    description: 'Donations awaiting compliance recompute',
    to: '/operations/queues',
    icon: RefreshCw,
  },
];

const RECENT_ACTIVITY_COUNT = 10;

function formatBadgeCount(badge: QueueBadge): string {
  return badge.truncated ? `${badge.count}+` : String(badge.count);
}

function outcomeTone(outcome: string): ChipTone {
  const normalized = outcome.toLowerCase();
  if (normalized === 'success' || normalized === 'allowed') return 'ok';
  if (normalized === 'denied' || normalized === 'failure' || normalized === 'error') return 'crit';
  return 'info';
}

export default function OperationsOverview() {
  const overview = useOverviewBadges();
  const audit = useAuditEvents({ limit: RECENT_ACTIVITY_COUNT });

  const queues = overview.data?.data.queues;
  const recentEvents = audit.rows.slice(0, RECENT_ACTIVITY_COUNT);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
          <Activity className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Operations Overview</h1>
          <p className="text-sm text-gray-text">
            Live queue depths across every admin review surface
          </p>
        </div>
      </div>

      {/* Queue tiles */}
      {overview.isError ? (
        <div className="glass-card p-6">
          <div className="flex items-center space-x-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>Failed to load queue counts: {overview.error.message}</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {QUEUE_TILES.map(tile => {
            const badge = queues?.[tile.key];
            const Icon = tile.icon;
            const hasBacklog = badge !== undefined && badge.count > 0;
            return (
              <Link
                key={tile.key}
                to={tile.to}
                className="glass-card p-5 hover:bg-gray-800/30 transition-colors block"
              >
                <div className="flex items-start justify-between">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      hasBacklog ? 'bg-yellow-500/10' : 'bg-primary/10'
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${hasBacklog ? 'text-yellow-400' : 'text-primary'}`}
                    />
                  </div>
                  {overview.isLoading || badge === undefined ? (
                    <div className="h-8 w-12 bg-dark-lighter rounded animate-pulse" />
                  ) : (
                    <span
                      className={`text-2xl font-bold ${
                        hasBacklog ? 'text-yellow-400' : 'text-white'
                      }`}
                    >
                      {formatBadgeCount(badge)}
                    </span>
                  )}
                </div>
                <h3 className="text-white font-medium mt-3">{tile.label}</h3>
                <p className="text-xs text-gray-text mt-1">{tile.description}</p>
              </Link>
            );
          })}
        </div>
      )}

      {/* Recent governance activity */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Recent Governance Activity</h2>
        {audit.isError ? (
          <div className="flex items-center space-x-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>Failed to load audit events: {audit.error.message}</span>
          </div>
        ) : audit.isPending ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }, (_, index) => (
              <div key={index} className="h-10 bg-dark-lighter rounded animate-pulse" />
            ))}
          </div>
        ) : recentEvents.length === 0 ? (
          <p className="text-sm text-gray-text">No governance events recorded yet.</p>
        ) : (
          <ul className="divide-y divide-gray-border/50">
            {recentEvents.map(event => (
              <li key={event.id} className="py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <StatusChip label={event.event_type} tone={outcomeTone(event.outcome)} />
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">
                      {event.action}
                      {event.target_type && event.target_id && (
                        <span className="text-gray-text">
                          {' '}
                          — {event.target_type} {event.target_id}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-text truncate">
                      {event.actor_type}
                      {event.actor_id ? ` · ${event.actor_id}` : ''}
                      {event.actor_role ? ` · ${event.actor_role}` : ''}
                    </p>
                  </div>
                </div>
                <span className="text-xs text-gray-text whitespace-nowrap flex-shrink-0">
                  {formatDistanceToNow(new Date(event.occurred_at), { addSuffix: true })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
